import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { User, AuthContextType, RegisterData, LoginData } from '../types';
import { apiService, API_BASE_URL } from '../services/api';
import { persistentCache } from '../services/persistentCache';
import { forServer } from '../utils/usda';

// Import the cache instance to clear it on logout
declare global {
  interface Window {
    apiCache?: {
      clear: () => void;
    };
  }
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Last-known user snapshot. Lets a returning user render the app instantly on
// open instead of staring at a full-screen spinner while /auth/me round-trips;
// the server copy still loads in the background and replaces it. api.ts clears
// this same key on a 401.
const USER_CACHE_KEY = 'user';

// Never kept on the device (Fulcrum #19): older server builds sent these in the user object.
const SECRET_USER_KEYS = ['password', 'emailVerificationToken', 'emailVerificationExpiry', 'resetPasswordToken', 'resetPasswordExpiry', 'iapPurchaseToken'];
const scrubSecrets = (u: any) => {
  if (!u || typeof u !== 'object') return u;
  let changed = false;
  for (const k of SECRET_USER_KEYS) if (k in u) { delete u[k]; changed = true; }
  if (u.emailPreferences && 'unsubscribeToken' in u.emailPreferences) { delete u.emailPreferences.unsubscribeToken; changed = true; }
  return changed;
};

const readCachedUser = (): User | null => {
  try {
    const raw = localStorage.getItem(USER_CACHE_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    // A snapshot saved by an older build may hold the password hash: clean it now.
    if (parsed && scrubSecrets(parsed)) localStorage.setItem(USER_CACHE_KEY, JSON.stringify(parsed));
    return parsed && parsed._id ? parsed : null;
  } catch {
    return null;
  }
};

const writeCachedUser = (user: User) => {
  try {
    // Keep billing details and push tokens out of storage — nothing needs them at boot
    const { billingAddress, ...snapshot } = user as any;
    scrubSecrets(snapshot);
    if (snapshot.notificationPreferences) {
      const { pushToken, apnsToken, ...prefs } = snapshot.notificationPreferences;
      snapshot.notificationPreferences = prefs;
    }
    localStorage.setItem(USER_CACHE_KEY, JSON.stringify(snapshot));
  } catch {
    // Storage full/unavailable — non-critical, next open just waits for /auth/me
  }
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [token, setToken] = useState<string | null>(localStorage.getItem('token'));
  const [user, setUser] = useState<User | null>(() => (localStorage.getItem('token') ? readCachedUser() : null));
  // Only block the UI on /auth/me when there is no cached user to render from
  const [loading, setLoading] = useState(() => !(localStorage.getItem('token') && readCachedUser()));
  const [error, setError] = useState<string | null>(null);
  const [isNewUser, setIsNewUser] = useState(false); // Track if user just registered
  const justAuthenticatedRef = useRef(false); // Skip getMe() after login/register

  useEffect(() => {
    const loadUser = async () => {
      // After login/register, user data is already set — skip the redundant getMe() call.
      // This prevents a race condition where getMe() fails (slow network, server lag)
      // and the catch block clears the token, logging the user out unexpectedly.
      if (justAuthenticatedRef.current) {
        justAuthenticatedRef.current = false;
        setLoading(false);
        return;
      }
      if (token) {
        apiService.setToken(token);
        try {
          const data = await apiService.getMe();
          // Ensure usage object exists
          const userWithUsage = {
            ...data.user,
            usage: data.user.usage || { aiFoodQueries: 0, aiAnalyses: 0, aiAssistantMessages: 0 },
          };
          setUser(userWithUsage);
        } catch (err: any) {
          const status = err?.response?.status;
          if (status === 401 || status === 404) {
            // Token is invalid, remove it
            localStorage.removeItem('token');
            localStorage.removeItem(USER_CACHE_KEY);
            setUser(null);
            setToken(null);
          }
          // Anything else (offline, timeout, 5xx) doesn't prove the session is
          // bad — keep the token and cached user so a weak signal can't log
          // someone out.
        }
      }
      setLoading(false);
    };

    loadUser();
  }, [token]);

  // Keep the snapshot current through every path that changes the user
  // (background /auth/me, login, profile edits, usage counters).
  useEffect(() => {
    if (user) writeCachedUser(user);
  }, [user]);

  // Session-expiry handler: the API interceptor dispatches 'auth:unauthorized'
  // on a 401 from a non-auth endpoint. Clear React state so ProtectedRoute
  // redirects to /login via the Router — no full-page reload.
  useEffect(() => {
    const handleUnauthorized = () => {
      setUser(null);
      setToken(null);
      apiService.setToken(null);
    };
    window.addEventListener('auth:unauthorized', handleUnauthorized);
    return () => window.removeEventListener('auth:unauthorized', handleUnauthorized);
  }, []);

  // Keep the server-side timezone in sync with the device so scheduled
  // coaching/reminders fire at the right local time. Runs at most once per session.
  const timezoneSyncedRef = useRef(false);
  useEffect(() => {
    if (!user || !token || timezoneSyncedRef.current) return;
    timezoneSyncedRef.current = true;
    try {
      const deviceTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      if (deviceTz && (user as any).timezone !== deviceTz) {
        apiService.updateProfile({ timezone: deviceTz } as any).catch(() => {
          // Non-fatal — retried next session
          timezoneSyncedRef.current = false;
        });
      }
    } catch { /* Intl unavailable — skip */ }
  }, [user, token]);

  // Apply onboarding data after signup
  useEffect(() => {
    if (!user || !token) return;
    const pending = localStorage.getItem('onboarding_pending_apply');
    if (pending !== 'true') return;

    localStorage.removeItem('onboarding_pending_apply');

    const applyOnboarding = async () => {
      try {
        // Apply profile data from questions step
        const questionsRaw = localStorage.getItem('onboarding_questions');
        if (questionsRaw) {
          const q = JSON.parse(questionsRaw);
          const profileUpdate: Record<string, any> = {};

          if (q.sex) profileUpdate.gender = q.sex;
          if (q.heightFt || q.heightIn) {
            const ft = parseInt(q.heightFt || '0', 10);
            const inches = parseInt(q.heightIn || '0', 10);
            profileUpdate.height = Math.round((ft * 12 + inches) * 2.54);
          }
          if (q.weight) {
            profileUpdate.weight = Math.round(parseFloat(q.weight) / 2.20462 * 10) / 10;
          }
          if (q.activityLevel) profileUpdate.activityLevel = q.activityLevel;
          if (q.healthGoals?.length) profileUpdate.healthGoals = q.healthGoals;

          if (Object.keys(profileUpdate).length > 0) {
            await apiService.updateProfile(profileUpdate);
            setUser(prev => prev ? { ...prev, ...profileUpdate } : prev);
          }
        }

        // Save demo meal as first food log via photo/confirm (handles flat food arrays)
        const demoResultRaw = localStorage.getItem('onboarding_demo_result');
        if (demoResultRaw) {
          try {
            const demoResult = JSON.parse(demoResultRaw);
            // Only their own meal, and only if they tapped "Save this meal" (2026-10-12):
            // the sample plate and an unsaved try used to land in the log too.
            const savedByThem = localStorage.getItem('onboarding_demo_saved') === 'true';
            if (demoResult.foods?.length > 0 && !demoResult.sample && savedByThem) {
              const today = new Date().toLocaleDateString('en-CA'); // local YYYY-MM-DD (not UTC)
              await fetch(`${API_BASE_URL}/ai-assistant/photo/confirm`, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify({
                  // USDA-matched demo foods carry client-only working data (_usda / _estimate).
                  foods: demoResult.foods.map(forServer),
                  mealType: demoResult.meal_type || demoResult.mealType || 'dinner',
                  clientDate: today,
                  clientTime: String(new Date().getHours()),
                }),
              });
            }
          } catch { /* Non-fatal */ }
        }

        // Trigger integration setup if selected (delayed to let food log save complete)
        const integration = localStorage.getItem('onboarding_integration');
        if (integration === 'garmin') {
          // No redirect (it skipped the setup screen and the offer): the setup screen
          // puts "Connect your Garmin" first instead (Jason, 2026-11-03).
          localStorage.setItem('fueltron_setup_watch_first', 'garmin');
        } else if (integration === 'apple_health') {
          // The onboarding watch screen promises "you'll connect it right after you sign up":
          // show Apple's Health permission sheet now (iPhone app only), same steps as the
          // Profile connection card. Non-fatal — the setup checklist still offers it.
          (async () => {
            try {
              const cap = await import('../utils/capacitor');
              if (!cap.isCapacitorNative() || cap.getCapacitorPlatform() !== 'ios') return;
              if (!(await cap.requestHealthKitAuthorization())) return;
              await cap.enableHealthKitBackgroundDelivery();
              await apiService.connectHealthKit();
              setUser(prev => (prev ? ({ ...prev, healthkitConnected: true } as any) : prev));
              const { syncHealthKitData } = await import('../services/healthKitSync');
              await syncHealthKitData(false);
            } catch { /* connect later from the setup checklist */ }
          })();
        }

        // Signal that onboarding was completed (suppresses AI welcome popup)
        sessionStorage.setItem('onboarding_completed', 'true');

        // Clean up onboarding localStorage
        localStorage.removeItem('onboarding_questions');
        localStorage.removeItem('onboarding_integration');
        localStorage.removeItem('onboarding_demo_result');
        localStorage.removeItem('onboarding_demo_saved');
        localStorage.removeItem('onboarding_demo_photo');
        localStorage.removeItem('onboarding_demo_used');
      } catch (err) {
        // Non-fatal — user can set profile data later
        console.error('Failed to apply onboarding data:', err);
      }
    };

    applyOnboarding();
  }, [user, token]);

  const login = async (credentials: LoginData) => {
    try {
      setError(null);
      
      // Clear API cache before login to ensure fresh data
      if (window.apiCache) {
        window.apiCache.clear();
      }
      
      const { token: newToken, user } = await apiService.login(credentials);
      localStorage.setItem('token', newToken);
      apiService.setToken(newToken);
      justAuthenticatedRef.current = true;
      setToken(newToken);
      const userWithUsage = {
        ...user,
        usage: user.usage || { aiFoodQueries: 0, aiAnalyses: 0, aiAssistantMessages: 0 },
      };
      setUser(userWithUsage);
      setIsNewUser(false); // Not a new user on login
    } catch (err: any) {
      setError(err.message);
      throw err;
    }
  };

  const register = async (data: RegisterData) => {
    try {
      setError(null);
      
      // Clear API cache before registration to ensure fresh data
      if (window.apiCache) {
        window.apiCache.clear();
      }
      
      const { token: newToken, user } = await apiService.register(data);
      localStorage.setItem('token', newToken);
      apiService.setToken(newToken);
      justAuthenticatedRef.current = true;
      setToken(newToken);
      const userWithUsage = {
        ...user,
        usage: user.usage || { aiFoodQueries: 0, aiAnalyses: 0, aiAssistantMessages: 0 },
      };
      setUser(userWithUsage);
      setIsNewUser(true); // Mark as new user after registration
      // Persist flag so tutorial can auto-start even after page reload
      localStorage.setItem('firstTimeUser', 'yes');
    } catch (err: any) {
      setError(err.message);
      throw err;
    }
  };

  const logout = () => {
    setUser(null);
    setToken(null);
    setIsNewUser(false);
    localStorage.removeItem('token');
    localStorage.removeItem(USER_CACHE_KEY);
    apiService.setToken(null);
    
    // Clear API cache to prevent data leakage between users
    if (window.apiCache) {
      window.apiCache.clear();
    }
    persistentCache.clearAll();
    // Clear all onboarding/session state for clean next-user experience
    localStorage.removeItem('firstTimeUser');
    localStorage.removeItem('aiWelcomeShown');
    localStorage.removeItem('welcomeShown');
    localStorage.removeItem('onboarding_pending_apply');
    localStorage.removeItem('onboarding_questions');
    localStorage.removeItem('onboarding_integration');
    localStorage.removeItem('fueltron_setup_watch_first');
    localStorage.removeItem('onboarding_demo_result');
    localStorage.removeItem('onboarding_demo_saved');
    localStorage.removeItem('onboarding_demo_photo');
    sessionStorage.clear(); // Wipe all session flags (aiWelcomeChecked, emailBannerDismissed, etc)
  };

  const clearNewUserFlag = () => {
    setIsNewUser(false);
  };

  const updateUser = async (data: Partial<User>) => {
    return new Promise<void>((resolve) => {
      if (user) {
        const updatedUser = { ...user, ...data };
        setUser(updatedUser);
        resolve();
      } else {
        resolve(); // Resolve even if there's no user, to not hang the await
      }
    });
  };

  const changePassword = async (currentPassword: string, newPassword: string) => {
    try {
      await apiService.changePassword(currentPassword, newPassword);
    } catch (err: any) {
      console.error('Failed to change password:', err);
      throw err;
    }
  };

  const loginWithGoogle = async (credential: string) => {
    try {
      setError(null);

      if (window.apiCache) {
        window.apiCache.clear();
      }

      const { token: newToken, user } = await apiService.loginWithGoogle(credential);
      localStorage.setItem('token', newToken);
      apiService.setToken(newToken);
      justAuthenticatedRef.current = true;
      setToken(newToken);
      const userWithUsage = {
        ...user,
        usage: user.usage || { aiFoodQueries: 0, aiAnalyses: 0, aiAssistantMessages: 0 },
      };
      setUser(userWithUsage);
      // Detect new Google sign-up: no profile data yet = new user
      if (!user.height && !user.weight) {
        setIsNewUser(true);
        localStorage.setItem('firstTimeUser', 'yes');
      } else {
        setIsNewUser(false);
      }
    } catch (err: any) {
      setError(err.message);
      throw err;
    }
  };

  const loginWithApple = async (data: { identityToken: string; fullName?: { givenName?: string; familyName?: string }; email?: string; authorizationCode?: string }) => {
    try {
      setError(null);

      if (window.apiCache) {
        window.apiCache.clear();
      }

      const { token: newToken, user } = await apiService.loginWithApple(data);
      localStorage.setItem('token', newToken);
      apiService.setToken(newToken);
      justAuthenticatedRef.current = true;
      setToken(newToken);
      const userWithUsage = {
        ...user,
        usage: user.usage || { aiFoodQueries: 0, aiAnalyses: 0, aiAssistantMessages: 0 },
      };
      setUser(userWithUsage);
      if (!user.height && !user.weight) {
        setIsNewUser(true);
        localStorage.setItem('firstTimeUser', 'yes');
      } else {
        setIsNewUser(false);
      }
    } catch (err: any) {
      setError(err.message);
      throw err;
    }
  };

  const deleteUser = async () => {
    try {
      await apiService.deleteAccount();
      logout();
    } catch (error) {
      console.error('Failed to delete account:', error);
      throw error;
    }
  };

  const value = {
    user,
    token,
    loading,
    error,
    isNewUser,
    login,
    register,
    logout,
    updateUser,
    changePassword,
    deleteUser,
    clearNewUserFlag,
    loginWithGoogle,
    loginWithApple,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}; 